import { MouseEventHandler, useEffect } from "react";
import { useQuery } from "@apollo/client";
import { Spacing, Button, Card, pxToRem, Heading } from "@ottatech/design";
import { Loading } from "@ottatech/shared-components";
import styled from "styled-components";

import { IDashboardPromptNotification } from "..";

import {
  Notification,
  ProfileStatus,
  ProfileCompletionStatusDocument,
} from "schema";
import { Delete as Cancel } from "components/Icons/Delete";
import { EditButton } from "pages/Profile/components/FormCard";
import { Link } from "components/Link";

const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  height: ${pxToRem(139)};
  width: 25rem;
  transition: 0.5s;
`;

const PromptHeader = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const ButtonWrap = styled(Button)`
  width: 100%;
`;

const DismissButtonWrap = styled.div`
  width: 1.5rem;
  margin-top: -0.3rem;
`;

export const formatMostRecentCompanies = (companies: string[]): string => {
  if (companies.length == 3) {
    return `${companies[0]}, ${companies[1]} and ${companies[2]}`;
  } else if (companies.length == 2) {
    return `${companies[0]} and ${companies[1]}`;
  }
  return companies[0];
};

const DismissButton = ({
  onClick,
}: {
  onClick?: MouseEventHandler;
}): React.ReactElement => {
  return (
    <DismissButtonWrap>
      <EditButton data-testid="dismiss-prompt-button" onClick={onClick}>
        <Cancel />
      </EditButton>
    </DismissButtonWrap>
  );
};

export const ProfilePerformanceContent = ({
  onLoad,
  onButtonClick,
  onDismiss,
}: {
  onLoad?: (profileStatus: Partial<ProfileStatus> | null) => void;
  onButtonClick?: () => void;
  onDismiss?: () => void;
}): React.ReactElement | null => {
  const { data: userProfileStatus, loading } = useQuery(
    ProfileCompletionStatusDocument,
    {
      fetchPolicy: "cache-and-network",
    }
  );
  const user = userProfileStatus?.me;

  const profileStatus: ProfileStatus | null =
    user?.__typename === "CurrentCandidate" ||
    (user?.__typename === "CurrentAdmin" && user.profileStatus)
      ? user.profileStatus
      : null;

  useEffect(() => {
    if (!loading && profileStatus) {
      onLoad?.({
        state: profileStatus.state,
        percent: profileStatus.percent,
        completedFields: profileStatus.completedFields,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, profileStatus]);

  if (loading) {
    return (
      <Card>
        <LoadingWrapper>
          <Loading />
        </LoadingWrapper>
      </Card>
    );
  }

  if (!profileStatus) {
    return null;
  }

  const companyViews = profileStatus?.mostRecentSourcingViews;
  const numCompanyViews = profileStatus?.numSourcingViews;
  const mostRecentCompanies = formatMostRecentCompanies(companyViews);

  const moreThanThreeCompaniesPromptContent: {
    bold: string;
    body: string;
    button: string;
  } = {
    bold: `${numCompanyViews} companies looked at your profile recently,`,
    body: `including ${mostRecentCompanies}. Improve it to get more companies interested in you.`,
    button: "Improve my profile",
  };

  const threeOrLessCompaniesPromptContent: {
    bold: string;
    body: string;
    button: string;
  } = {
    bold: `${mostRecentCompanies} looked at your profile recently.`,
    body: `Improve it to get more companies interested in you.`,
    button: "Improve my profile",
  };

  const promptContent =
    numCompanyViews > 3
      ? moreThanThreeCompaniesPromptContent
      : threeOrLessCompaniesPromptContent;

  return (
    <Card data-testid="profile-performance">
      <Spacing size={-1}>
        <PromptHeader>
          <Heading size={1} bold>
            How&apos;s your profile doing?
          </Heading>
          <DismissButton onClick={onDismiss} />
        </PromptHeader>
        <p>
          <strong>{promptContent.bold}</strong> {promptContent.body}
        </p>
        {promptContent.button && (
          <Link to="/profile" underline={false}>
            <ButtonWrap
              data-testid="view-profile-button"
              level="secondary"
              onClick={onButtonClick}
            >
              {promptContent.button}
            </ButtonWrap>
          </Link>
        )}
      </Spacing>
    </Card>
  );
};

export const ProfilePerformanceNotification: IDashboardPromptNotification = {
  key: "profile-performance-prompt-notification",
  level: "neutral-grey",
  name: Notification.ProfilePerformance,
  Content: ProfilePerformanceContent,
};
